import { useState, useEffect } from "react";
import { useCurrency } from "@/packages/context/CurrencyContext";
import { useGeneralSettings } from "@/packages/context/GeneralSettingContext";
import { fetchAllCountries } from "@/api-service/address";
import { Country } from "@/packages/types/address";
import { useCountry } from "@/packages/context/CountryContext";

let countriesCache: Country[] | null = null;

export const useResolvedCountryName = (): string => {
  const { id: currencyId } = useCurrency();
  const { shipping } = useGeneralSettings();
  const { selectedCountryName } = useCountry();
  const [countries, setCountries] = useState<Country[]>(countriesCache || []);
  const [countryName, setCountryName] = useState<string>("");

  useEffect(() => {
    if (countriesCache) {
      setCountries(countriesCache);
      return;
    }
    fetchAllCountries()
      .then((res) => {
        if (res?.data) {
          countriesCache = res.data;
          setCountries(res.data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch countries list in hook:", err);
      });
  }, []);

  useEffect(() => {
    // 1. Prioritize explicit user-selected country name from CountryContext
    if (selectedCountryName) {
      setCountryName(selectedCountryName);
      return;
    }

    if (!currencyId || !shipping?.countryShipping || countries.length === 0) {
      setCountryName("");
      return;
    }

    // Find all countryShipping records matching the current currencyId
    const matchedShippings = shipping.countryShipping.filter(
      (item) => Number(item.currencyId) === Number(currencyId)
    );

    // If no matching countries, or more than one country shares this currency
    if (matchedShippings.length !== 1) {
      setCountryName("");
      return;
    }

    const countryId = matchedShippings[0].countryId;
    const matchedCountry = countries.find(
      (c) => Number(c.id) === Number(countryId)
    );

    if (matchedCountry) {
      setCountryName(matchedCountry.name);
    } else {
      setCountryName("");
    }
  }, [currencyId, shipping, countries, selectedCountryName]);

  return countryName;
};

